import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { Icon } from '@/components/brand/Brand'
import { GlassPanel } from '@/components/sections/GlassPanel'
import { cx } from '@/lib/cx'

/* The private partner briefing (first-two-months page): the design partnership explained in
   stages, the work, the outcomes and what both sides should have at the end. */

export type PartnerBriefingStage = {
  number: string
  timing: string
  title: string
  body: string
}

export type PartnerBriefingWorkItem = {
  number: string
  title: string
  outcome: string
  points: Array<{
    label: string
    body: string
  }>
}

export type PartnerBriefingOutcome = {
  number: string
  title: string
  body: string
}

export type PartnerBriefingClosingItem = {
  lead: string
  detail: string
}

type PartnerBriefingProps = {
  titleId: string
  eyebrow: string
  title: string
  intro: ReactNode[]
  stagesTitle: string
  stages: PartnerBriefingStage[]
  approachEyebrow: string
  approachTitle: string
  approachBody: ReactNode[]
  workItems: PartnerBriefingWorkItem[]
  outcomesEyebrow: string
  outcomesTitle: string
  outcomes: PartnerBriefingOutcome[]
  calculator: {
    title: string
    body: string
    action: ReactNode
  }
  closingEyebrow: string
  closingTitle: string
  closingItems: PartnerBriefingClosingItem[]
  closingBody: ReactNode
}

export const PartnerBriefing = ({
  titleId,
  eyebrow,
  title,
  intro,
  stagesTitle,
  stages,
  approachEyebrow,
  approachTitle,
  approachBody,
  workItems,
  outcomesEyebrow,
  outcomesTitle,
  outcomes,
  calculator,
  closingEyebrow,
  closingTitle,
  closingItems,
  closingBody,
}: PartnerBriefingProps) => {
  const [activeWorkIndex, setActiveWorkIndex] = useState(0)
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
  const activeWorkItem = workItems[activeWorkIndex]

  // Arrow keys move between the tabs, as in any tab list.
  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key]
    if (!step) return
    event.preventDefault()
    const next = (index + step + workItems.length) % workItems.length
    setActiveWorkIndex(next)
    tabRefs.current[next]?.focus()
  }

  return (
    <article className="brief" aria-labelledby={titleId}>
      {/* The glass stays still until the pointer stirs it: this page has no pause control. */}
      <GlassPanel className="hero hero--text" shade="hero" labelledBy={titleId} ambient={false}>
        <div className="nav-space" aria-hidden="true" />
        <div className="hero-text">
          <div className="hero-text-copy">
            <p className="x-eyebrow x-eyebrow--ink">{eyebrow}</p>
            <h1 id={titleId} className="display">
              {title}
            </h1>
            <div className="brief-intro">
              {intro.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      </GlassPanel>

      <section id="briefing-stages" className="brief-sec" aria-label={stagesTitle}>
        <div className="x-wrap">
          <p className="brief-annotation">{stagesTitle}</p>
          <div className="brief-stages">
            {stages.map((stage, index) => (
              <article className={cx('brief-stage', index === 0 && 'is-now')} key={stage.number}>
                <p className="brief-stage-meta">
                  <span className="brief-n">{stage.number}</span>
                  <span>{stage.timing}</span>
                </p>
                <h3>{stage.title}</h3>
                <p>{stage.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="briefing-work" className="brief-sec" aria-labelledby="briefing-approach-title">
        <div className="x-wrap">
          <div className="brief-split">
            <div className="brief-split-head">
              <p className="x-eyebrow x-eyebrow--ink">{approachEyebrow}</p>
              <h2 id="briefing-approach-title" className="brief-h2">
                {approachTitle}
              </h2>
            </div>
            <div className="brief-copy">
              {approachBody.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>

          <div className="brief-workbench">
            <div className="brief-tabs" role="tablist" aria-label={approachEyebrow} aria-orientation="vertical">
              {workItems.map((item, index) => {
                const isActive = index === activeWorkIndex

                return (
                  <button
                    key={item.number}
                    ref={(element) => {
                      tabRefs.current[index] = element
                    }}
                    id={`briefing-work-tab-${item.number}`}
                    className="brief-tab"
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`briefing-work-panel-${item.number}`}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => setActiveWorkIndex(index)}
                    onKeyDown={(event) => onTabKeyDown(event, index)}
                  >
                    <span className="brief-n">{item.number}</span>
                    <strong>{item.title}</strong>
                    <Icon name="arrow" />
                  </button>
                )
              })}
            </div>

            {activeWorkItem ? (
              <div
                key={activeWorkItem.number}
                id={`briefing-work-panel-${activeWorkItem.number}`}
                className="brief-panel"
                role="tabpanel"
                aria-labelledby={`briefing-work-tab-${activeWorkItem.number}`}
                tabIndex={0}
              >
                <p className="brief-panel-outcome">{activeWorkItem.outcome}</p>
                <ul className="brief-points">
                  {activeWorkItem.points.map((point) => (
                    <li key={point.label}>
                      <strong>{point.label}:</strong> {point.body}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section id="briefing-outcomes" className="brief-sec" aria-labelledby="briefing-outcomes-title">
        <div className="x-wrap">
          <div className="brief-head">
            <p className="x-eyebrow x-eyebrow--ink">{outcomesEyebrow}</p>
            <h2 id="briefing-outcomes-title" className="x-h2">
              {outcomesTitle}
            </h2>
          </div>
          <div className="brief-outcomes">
            {outcomes.map((outcome) => (
              <article className="brief-outcome" key={outcome.number}>
                <span className="brief-n">{outcome.number}</span>
                <h3>{outcome.title}</h3>
                <p>{outcome.body}</p>
              </article>
            ))}
          </div>
          <aside className="brief-calculator">
            <div>
              <h3>{calculator.title}</h3>
              <p>{calculator.body}</p>
            </div>
            {calculator.action}
          </aside>
        </div>
      </section>

      <section id="briefing-closing" className="brief-closing" aria-labelledby="briefing-closing-title">
        <div className="x-wrap brief-split">
          <div className="brief-split-head">
            <p className="x-eyebrow">{closingEyebrow}</p>
            <h2 id="briefing-closing-title" className="brief-closing-title">
              {closingTitle}
            </h2>
          </div>
          <div className="brief-closing-copy">
            <ul className="brief-checks">
              {closingItems.map((item) => (
                <li key={item.lead}>
                  <span className="brief-check" aria-hidden="true">
                    <Icon name="check" />
                  </span>
                  <span>
                    <strong>{item.lead}</strong> {item.detail}
                  </span>
                </li>
              ))}
            </ul>
            <p>{closingBody}</p>
          </div>
        </div>
      </section>
    </article>
  )
}
